import React, { useState } from 'react'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material'
import axiosInstance from '../../../utils/axiosInstance'
import SuccessModal from '../../../components/SuccessModal'

interface Recipient {
  email: string
  customerId: string
  customerName: string
}

interface Preview {
  recipients: Recipient[]
  lastSentAt: string | null
}

interface NewLeadNotificationButtonProps {
  leadId: string
  onSent?: () => void
}

// Admin tool: resend the "you have a new lead" email to the lead's customer (and its
// reseller). Opens a confirmation listing the recipients and when it was last sent, so the
// customer is not notified twice by accident.
const NewLeadNotificationButton: React.FC<NewLeadNotificationButtonProps> = ({
  leadId,
  onSent,
}) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [preview, setPreview] = useState<Preview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const handleOpen = async () => {
    setOpen(true)
    setLoading(true)
    setError(null)
    setPreview(null)
    try {
      const { data } = await axiosInstance.get(`/lead/${leadId}/new-lead-notification`)
      setPreview(data)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load the notification recipients')
    } finally {
      setLoading(false)
    }
  }

  const handleSend = async () => {
    setSending(true)
    setError(null)
    try {
      const { data } = await axiosInstance.post(`/lead/${leadId}/send-new-lead-notification`)
      const results: Array<{ status: string }> = data.results || []
      const failed = results.filter((r) => r.status === 'failed').length
      setOpen(false)
      setSuccessMessage(
        failed
          ? `Queued for ${results.length - failed} of ${
              results.length
            } recipients. ${failed} failed, see the email history.`
          : `New lead notification queued for ${results.length} recipient${
              results.length === 1 ? '' : 's'
            }`
      )
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to send the new lead notification')
    } finally {
      setSending(false)
    }
  }

  const hasRecipients = !!preview && preview.recipients.length > 0

  // Refresh only once the message is dismissed: the parent's refetch briefly unmounts the
  // lead dialog (and this component with it), which would swallow the message.
  const handleSuccessClose = () => {
    setSuccessMessage(null)
    if (onSent) onSent()
  }

  return (
    <>
      <Button
        variant="outlined"
        color="secondary"
        startIcon={<NotificationsActiveIcon />}
        onClick={handleOpen}
      >
        Notify customer
      </Button>

      <Dialog open={open} onClose={() => !sending && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send new lead notification</DialogTitle>
        <DialogContent dividers>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {preview?.lastSentAt && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              A new lead notification was already sent for this lead on{' '}
              {new Date(preview.lastSentAt).toLocaleString()}.
            </Alert>
          )}

          {preview && !hasRecipients && (
            <Alert severity="error">
              The customer has no email address on file, so there is nobody to notify.
            </Alert>
          )}

          {hasRecipients && (
            <>
              <Typography variant="body2" gutterBottom>
                This emails the customer that a new lead came in, with a link to open it. It goes to
                the notification addresses on the customer record (and on its reseller, if it has
                one):
              </Typography>
              <List dense disablePadding>
                {preview.recipients.map((recipient) => (
                  <ListItem key={`${recipient.customerId}-${recipient.email}`} disableGutters>
                    <ListItemText primary={recipient.email} secondary={recipient.customerName} />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="inherit" disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            variant="contained"
            color="primary"
            disabled={!hasRecipients || sending}
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <SuccessModal
        successModalOpen={!!successMessage}
        // SuccessModal calls both callbacks on close; only one may trigger the refetch.
        setSuccessModalOpen={() => setSuccessMessage(null)}
        handleClose={handleSuccessClose}
        message={successMessage || ''}
      />
    </>
  )
}

export default NewLeadNotificationButton
